import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PRIVACY, TERMS, DISCLAIMER, ACCESSIBILITY, type LegalDoc } from '../content/legal';
import { PRACTICE, FOOTER } from '../content/site';
import { Eyebrow } from '../components/ui/Primitives';
export type LegalKind = 'privacy' | 'terms' | 'disclaimer' | 'accessibility';
const DOCS = {
  privacy: PRIVACY,
  terms: TERMS,
  disclaimer: DISCLAIMER,
  accessibility: ACCESSIBILITY
};
export function LegalPage({
  kind
}: {
  kind: LegalKind;
}) {
  const {
    language
  } = useLanguage();
  const doc: LegalDoc = DOCS[kind][language];
  const footer = FOOTER[language];
  const siblings: {
    to: string;
    label: string;
    key: LegalKind;
  }[] = [{
    to: '/privacy',
    label: footer.links.privacy,
    key: 'privacy'
  }, {
    to: '/terms',
    label: footer.links.terms,
    key: 'terms'
  }, {
    to: '/disclaimer',
    label: footer.links.disclaimer,
    key: 'disclaimer'
  }, {
    to: '/accessibility',
    label: footer.links.accessibility,
    key: 'accessibility'
  }];
  return <>
      <section className="border-b border-espresso/10 bg-sand/40 px-5 py-16 sm:px-8 lg:px-10 lg:py-20" data-id="element-333" data-mp="1me12z43qleyh">
        <div className="mx-auto w-full max-w-3xl" data-id="element-334" data-mp="18f1ebwg8502b">
          <Eyebrow data-id="element-335" data-mp="18e8ivrmptwzh">{footer.legalLinks}</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl font-light leading-[1.05] tracking-tight text-espresso sm:text-5xl" data-id="element-336" data-mp="1dd37ja7d6fri">
            {doc.title}
          </h1>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-espresso/40" data-id="element-337" data-mp="ymlyb4ohmlk2">
            {doc.updated}
          </p>
        </div>
      </section>

      <section className="bg-cream px-5 py-16 sm:px-8 lg:px-10 lg:py-24" data-id="element-338" data-mp="ppbj588juc9v">
        <div className="mx-auto w-full max-w-3xl" data-id="element-339" data-mp="tou6sbrr91ge">
          <p className="font-serif text-lg font-light leading-relaxed text-espresso/80 lg:text-xl" data-id="element-340" data-mp="tnj3se64vxqc">
            {doc.intro}
          </p>

          <div className="prose-legal mt-12" data-id="element-341" data-mp="kpgsn5soropc">
            {doc.blocks.map((block, i) => {
            if (block.type === 'h2') {
              return <h2 key={i} data-id="element-342" data-mp="2mfo4qhet87f8">{block.text}</h2>;
            }
            if (block.type === 'ul') {
              return <ul key={i} data-id="element-343" data-mp="2hljwlutt5tck">
                    {block.items.map(item => <li key={item} data-id="element-344" data-mp="2djouio41zbxq">{item}</li>)}
                  </ul>;
            }
            return <p key={i} data-id="element-345" data-mp="35cpa43ca09jp" data-mpi={i}>{block.text}</p>;
          })}
          </div>

          <div className="mt-14 rounded-2xl border border-espresso/10 bg-white p-7" data-id="element-346" data-mp="35auwzb78xlq6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-espresso/40" data-id="element-347" data-mp="31c5bswfyuzvz">
              {footer.contact}
            </p>
            <p className="mt-3 text-sm font-light leading-relaxed text-espresso/70" data-id="element-348" data-mp="2rdg1ppubajqa">
              {PRACTICE.address}
              <br data-id="element-349" data-mp="1kxdsd90st2yt" />
              <a href={`mailto:${PRACTICE.email}`} className="text-bronze underline underline-offset-4" data-id="element-350" data-mp="1kyoyhfhpumt3">
                {PRACTICE.email}
              </a>
            </p>
          </div>

          <nav aria-label={footer.legalLinks} className="mt-12 flex flex-wrap gap-x-6 gap-y-3 border-t border-espresso/10 pt-8" data-id="element-351" data-mp="15s9g1egbmcte">
            {siblings.map(item => <Link key={item.key} to={item.to} className={`text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors ${item.key === kind ? 'text-espresso' : 'text-espresso/45 hover:text-espresso'}`} data-id="element-352" data-mp="1kj06ai29l3ue">
                {item.label}
              </Link>)}
          </nav>
        </div>
      </section>
    </>;
}